<script setup lang="ts">
  import { ref } from 'vue';
  import { FormInput, SelectInput, DatePicker, IconImage, Switch } from '@/shared/ui';
  import { NO_SPACES_MASK, MASK_PRESETS } from '@/shared/lib/mask';
  import CalendarIcon from '@/shared/assets/icons/CalendarIcon.svg';
  import AddButton from '@/shared/assets/icons/AddButton.svg';
  import DeleteButton from '@/shared/assets/icons/DeleteButton.svg';

  const MAX_USER_NAME_LENGTH = 80;

  interface Option {
    id: string | number;
    name: string;
  }

  export interface PositionItem {
    departmentId: string | number;
    positionId: string | number;
  }

  export interface FunctionalActivityItem {
    functionalActivityId: string | number;
  }

  interface Props {
    name: string;
    registrationNumber: string;
    username: string;
    email: string;
    positions: PositionItem[];
    functionalActivities: FunctionalActivityItem[];
    admissionDate: Date | null;
    dismissalDate: Date | null;
    status: string;
    isSuperadmin: boolean;
    showSuperadminToggle: boolean;
    departmentOptions: Option[];
    getPositionOptions: (departmentId: string | number) => Option[];
    functionalActivityOptions: Option[];
    isLoadingDepartments: boolean;
    isLoadingPositions: boolean;
    isLoadingActivities: boolean;
    usernameError?: string;
    emailError?: string;
    admissionDateError?: string;
    dismissalDateError?: string;
    registrationNumberError?: string;
    showPositionSection?: boolean;
  }

  const props = withDefaults(defineProps<Props>(), {
    isLoadingDepartments: false,
    isLoadingPositions: false,
    isLoadingActivities: false,
    usernameError: '',
    emailError: '',
    admissionDateError: '',
    dismissalDateError: '',
    registrationNumberError: '',
    showSuperadminToggle: false,
    showPositionSection: false,
  });

  const emit = defineEmits<{
    (e: 'update:name', value: string): void;
    (e: 'update:registrationNumber', value: string): void;
    (e: 'update:username', value: string): void;
    (e: 'update:email', value: string): void;
    (e: 'update:positions', value: PositionItem[]): void;
    (e: 'update:functionalActivities', value: FunctionalActivityItem[]): void;
    (e: 'update:admissionDate', value: Date | null): void;
    (e: 'update:dismissalDate', value: Date | null): void;
    (e: 'update:status', value: string): void;
    (e: 'update:isSuperadmin', value: boolean): void;
    (e: 'loadPositions', departmentId: number): void;
  }>();

  const admissionDatePickerOpen = ref(false);
  const dismissalDatePickerOpen = ref(false);

  const handleRegistrationNumberUpdate = (value: string) => {
    emit('update:registrationNumber', value);
  };

  const formatDate = (date: Date | null): string => {
    if (!date) return 'Selecione uma data';
    if (Number.isNaN(date.getTime())) return 'Selecione uma data';
    return date.toLocaleDateString('pt-BR');
  };

  const updatePositionDepartment = (index: number, departmentId: string | number) => {
    const newPositions = [...props.positions];
    newPositions[index] = { ...newPositions[index], departmentId, positionId: '' };
    emit('update:positions', newPositions);
    if (departmentId) {
      emit('loadPositions', Number(departmentId));
    }
  };

  const updatePositionId = (index: number, positionId: string | number) => {
    const newPositions = [...props.positions];
    newPositions[index] = { ...newPositions[index], positionId };
    emit('update:positions', newPositions);
  };

  const addPosition = () => {
    const newPositions = [...props.positions, { departmentId: '', positionId: '' }];
    emit('update:positions', newPositions);
  };

  const removePosition = (index: number) => {
    const newPositions = props.positions.filter((_, i) => i !== index);
    emit('update:positions', newPositions);
  };

  const updateFunctionalActivity = (index: number, functionalActivityId: string | number) => {
    const newActivities = [...props.functionalActivities];
    newActivities[index] = { functionalActivityId };
    emit('update:functionalActivities', newActivities);
  };

  const addFunctionalActivity = () => {
    const newActivities = [...props.functionalActivities, { functionalActivityId: '' }];
    emit('update:functionalActivities', newActivities);
  };

  const removeFunctionalActivity = (index: number) => {
    const newActivities = props.functionalActivities.filter((_, i) => i !== index);
    emit('update:functionalActivities', newActivities);
  };

  const setAdmissionDate = (date: Date | null) => {
    emit('update:admissionDate', date);
  };

  const setAdmissionDatePickerOpen = (isOpen: boolean) => {
    admissionDatePickerOpen.value = isOpen;
  };

  const setDismissalDate = (date: Date | null) => {
    emit('update:dismissalDate', date);
  };

  const setDismissalDatePickerOpen = (isOpen: boolean) => {
    dismissalDatePickerOpen.value = isOpen;
  };
</script>

<template>
  <div class="px-6 py-6 space-y-6">
    <div class="flex gap-4">
      <div class="flex-1">
        <FormInput
          id="name"
          :model-value="name"
          label="Nome completo:"
          type="text"
          placeholder="Escreva o nome do novo colaborador"
          label-class="text-sm text-gray-07 font-semibold"
          :max-length="MAX_USER_NAME_LENGTH"
          @update:model-value="$emit('update:name', $event)"
        />
      </div>
      <div class="w-44">
        <FormInput
          id="registrationNumber"
          :model-value="registrationNumber"
          label="Número de Matrícula:"
          type="text"
          placeholder="000000"
          :max-length="6"
          :mask="{ mask: MASK_PRESETS.register_number }"
          label-class="text-sm text-gray-07 font-semibold"
          :error="props.registrationNumberError"
          @update:model-value="handleRegistrationNumberUpdate($event)"
        />
      </div>
    </div>

    <hr class="border-t border-gray-02">

    <div class="grid grid-cols-2 gap-4">
      <FormInput
        id="username"
        :model-value="username"
        label="Login:"
        type="text"
        placeholder="Login do colaborador"
        label-class="text-sm text-gray-07 font-semibold"
        :error="props.usernameError"
        :mask="NO_SPACES_MASK"
        @update:model-value="$emit('update:username', $event)"
      />

      <FormInput
        id="email"
        :model-value="email"
        label="Email:"
        type="email"
        placeholder="Email do colaborador"
        label-class="text-sm text-gray-07 font-semibold"
        :error="props.emailError"
        @update:model-value="$emit('update:email', $event)"
      />
    </div>
    <!--
      SEÇÃO OCULTA TEMPORARIAMENTE: Departamento/Cargo/Atividade Funcional
      Para reativar, altere showPositionSection para true
    -->
    <template v-if="props.showPositionSection">
      <hr class="border-t border-gray-02">

      <div class="space-y-4">
        <div
          v-for="(position, index) in positions"
          :key="index"
          class="flex gap-4 items-end"
        >
          <div class="w-64 flex flex-col gap-2">
            <label
              v-if="index === 0"
              class="text-sm text-gray-07 font-semibold"
            >Departamento:</label>
            <SelectInput
              :model-value="position.departmentId"
              :options="departmentOptions"
              :disabled="isLoadingDepartments"
              :label="isLoadingDepartments ? 'Carregando...' : 'Selecione um Departamento'"
              container-class="w-full"
              max-height="150px"
              @update:model-value="updatePositionDepartment(index, $event)"
            />
          </div>

          <div class="flex-1 flex flex-col gap-2">
            <label
              v-if="index === 0"
              class="text-sm text-gray-07 font-semibold"
            >Cargo:</label>
            <SelectInput
              :model-value="position.positionId"
              :options="getPositionOptions(position.departmentId)"
              :disabled="!position.departmentId || isLoadingPositions || getPositionOptions(position.departmentId).length === 0"
              :label="isLoadingPositions ? 'Carregando...' : 'Selecione um Cargo'"
              container-class="w-full"
              max-height="150px"
              @update:model-value="updatePositionId(index, $event)"
            />
          </div>

          <div class="flex items-center h-[42px]">
            <IconImage
              v-if="index === 0"
              :src="AddButton"
              alt="Adicionar cargo"
              class="cursor-pointer text-[var(--theme-secondary-ink)] hover:opacity-80"
              @click="addPosition"
            />
            <IconImage
              v-else
              :src="DeleteButton"
              alt="Remover cargo"
              class="cursor-pointer text-gray-05 hover:text-gray-07"
              @click="removePosition(index)"
            />
          </div>
        </div>
      </div>

      <hr class="border-t border-gray-02">

      <div class="space-y-4">
        <div
          v-for="(activity, index) in functionalActivities"
          :key="index"
          class="flex gap-4 items-end"
        >
          <div class="flex-1 flex flex-col gap-2">
            <label
              v-if="index === 0"
              class="text-sm text-gray-07 font-semibold"
            >Atividade Funcional:</label>
            <SelectInput
              :model-value="activity.functionalActivityId"
              :options="functionalActivityOptions"
              :disabled="isLoadingActivities"
              :label="isLoadingActivities ? 'Carregando...' : 'Selecione uma Atividade'"
              container-class="w-full"
              max-height="150px"
              @update:model-value="updateFunctionalActivity(index, $event)"
            />
          </div>

          <div class="flex items-center h-[42px]">
            <IconImage
              v-if="index === 0"
              :src="AddButton"
              alt="Adicionar atividade funcional"
              class="cursor-pointer text-[var(--theme-secondary-ink)] hover:opacity-80"
              @click="addFunctionalActivity"
            />
            <IconImage
              v-else
              :src="DeleteButton"
              alt="Remover atividade funcional"
              class="cursor-pointer text-gray-05 hover:text-gray-07"
              @click="removeFunctionalActivity(index)"
            />
          </div>
        </div>
      </div>
    </template>

    <div class="flex gap-8">
      <div class="flex flex-col gap-2 w-64">
        <label class="text-sm text-gray-07 font-semibold">Data de admissão:</label>
        <button
          type="button"
          class="flex w-full items-center justify-between gap-2 bg-white-01 px-4 py-2.5 border rounded-lg text-gray-07 text-sm text-left focus:outline-none focus:border-gray-07"
          :class="props.admissionDateError ? 'border-red-16' : 'border-gray-02'"
          @click="admissionDatePickerOpen = true"
        >
          <IconImage
            :src="CalendarIcon"
            alt="Calendário"
            class="h-4.5 w-4.5"
          />
          <span class="flex-1">{{ formatDate(admissionDate) }}</span>
        </button>
        <DatePicker
          v-if="admissionDatePickerOpen"
          :selected-date="admissionDate"
          :is-open="admissionDatePickerOpen"
          :default-to-today="false"
          @update:selected-date="setAdmissionDate"
          @update:is-open="setAdmissionDatePickerOpen"
        />
        <p
          v-if="props.admissionDateError"
          class="text-red-16 text-sm/5"
        >
          {{ props.admissionDateError }}
        </p>
      </div>

      <div class="flex flex-col gap-2 w-64">
        <label class="text-sm text-gray-07 font-semibold">Data de demissão:</label>
        <button
          type="button"
          class="flex w-full items-center justify-between gap-2 bg-white-01 px-4 py-2.5 border rounded-lg text-gray-07 text-sm text-left focus:outline-none focus:border-gray-07"
          :class="props.dismissalDateError ? 'border-red-16' : 'border-gray-02'"
          @click="dismissalDatePickerOpen = true"
        >
          <IconImage
            :src="CalendarIcon"
            alt="Calendário"
            class="h-4.5 w-4.5"
          />
          <span class="flex-1">{{ formatDate(dismissalDate) }}</span>
        </button>
        <DatePicker
          v-if="dismissalDatePickerOpen"
          :selected-date="dismissalDate"
          :is-open="dismissalDatePickerOpen"
          :default-to-today="false"
          @update:selected-date="setDismissalDate"
          @update:is-open="setDismissalDatePickerOpen"
        />
        <p
          v-if="props.dismissalDateError"
          class="text-red-16 text-sm/5"
        >
          {{ props.dismissalDateError }}
        </p>
      </div>
    </div>

    <hr class="border-t border-gray-02">

    <div class="flex flex-col gap-2">
      <label class="text-sm text-gray-07 font-semibold">Status:</label>
      <div class="flex gap-6">
        <label class="flex items-center gap-2 cursor-pointer">
          <input
            type="radio"
            name="status"
            value="ACTIVE"
            :checked="status === 'ACTIVE'"
            class="w-4 h-4 accent-[var(--theme-secondary)] border-gray-03 focus:ring-[var(--theme-secondary-ink)]"
            @change="$emit('update:status', 'ACTIVE')"
          >
          <span class="text-sm text-gray-07">Ativo</span>
        </label>
        <label class="flex items-center gap-2 cursor-pointer">
          <input
            type="radio"
            name="status"
            value="INACTIVE"
            :checked="status === 'INACTIVE'"
            class="w-4 h-4 accent-[var(--theme-secondary)] border-gray-03 focus:ring-[var(--theme-secondary-ink)]"
            @change="$emit('update:status', 'INACTIVE')"
          >
          <span class="text-sm text-gray-07">Inativo</span>
        </label>
      </div>
    </div>

    <template v-if="showSuperadminToggle">
      <hr class="border-t border-gray-02">

      <div class="flex items-center gap-3">
        <span class="text-sm font-semibold text-gray-07">Superadmin:</span>
        <Switch
          :model-value="isSuperadmin"
          aria-label="Superadmin"
          @update:model-value="$emit('update:isSuperadmin', $event)"
        />
      </div>
    </template>
  </div>
</template>
